/**
 * BoxPlotPreview Component
 * Handles rendering for box plots with proper sizing
 */

import { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridRows } from '@visx/grid';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { useChartTooltip } from './useChartTooltip';
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import ChartTitle from './ChartTitle';
import {
  resolveXAxisLabels,
  makeTickComponent,
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  niceDomain,
  getCompactTickFormatter,
} from '../../utils/chartLayout';
import { getColors } from '../../charts/shared/palettes';

function BoxPlotPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();

  const data = (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0)
    ? transformData(userData, mapping, chartType.id)
    : chartType.sampleData;

  // Adaptive layout
  const categories = data.map(d => d.category);
  const xAxis = resolveXAxisLabels({
    labels: categories,
    chartWidth: width,
    chartHeight: height,
    baseFontSize: theme.tickSize ?? 13,
    fontFamily: theme.fontBody,
    rotationOverride: config.labelRotation,
    truncateLabels: !!config.truncateXAxisLabels,
  });

  // Get colors for each category based on palette selection
  const categoryColors = useMemo(() => {
    return getColors(config, data.length, theme);
  }, [config, data.length, theme]);

  // Calculate Y domain
  const yDomain = useMemo(() => {
    const allValues = data.flatMap(d => [d.min, d.max, ...d.outliers]);
    return niceDomain(allValues, { padding: 0.1, minOverride: config.yMin, maxOverride: config.yMax });
  }, [data, config.yMin, config.yMax]);
  const yTicksForLayout = useMemo(
    () => scaleLinear({ domain: yDomain, range: [100, 0] }).ticks(),
    [yDomain],
  );
  const { margin, innerWidth, innerHeight } = computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    xLabels: categories,
    yTickValues: yTicksForLayout,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: !!config.xlabel,
    hasYLabel: !!config.ylabel,
    orientation: 'vertical',
    baseRight: 80,
    baseBottom: 70,
    xAxisResolved: xAxis,
  });

  // visx band scale for categories (x-axis)
  const xScale = useMemo(() => scaleBand({
    domain: data.map(d => d.category),
    range: [0, innerWidth],
    padding: 0,
  }), [data, innerWidth]);

  // visx linear scale for values (y-axis)
  const yScale = useMemo(() => scaleLinear({
    domain: yDomain,
    range: [innerHeight, 0],
  }), [yDomain, innerHeight]);

  // Generate Y-axis ticks to match original evenly-spaced layout
  const yTicks = useMemo(() => {
    const [yMin, yMax] = yDomain;
    const tickCount = 5;
    const step = (yMax - yMin) / (tickCount - 1);
    return Array.from({ length: tickCount }, (_, i) => Math.round(yMin + step * i));
  }, [yDomain]);
  const spacingSpec = getAxisSpacingSpec(theme);
  const {
    xTickGap,
    yTickGap,
    yTickBaselineNudge,
    xTickLength,
    yTickLength,
  } = spacingSpec;
  const axisDebugEnabled = isAxisDebugEnabled();
  const xLanes = computeAxisLanes({
    axisSide: 'bottom',
    labels: categories.map((c) => xAxis.tickFormat(c)),
    tickFontSize: xAxis.fontSize,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    rotation: xAxis.rotation || 0,
    showTicks: true,
    tickLength: xTickLength,
    spacingSpec: { ...spacingSpec, gapX: xTickGap },
    telemetryMeta: { chartId: 'box-plot', viewportWidth: width, viewportHeight: height },
  });
  const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;
  const yLanes = computeAxisLanes({
    axisSide: 'left',
    labels: yTicks.map((v) => String(v)),
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    tickLength: yTickLength,
    availableMargin: margin.left,
    spacingSpec: { ...spacingSpec, gapY: yTickGap },
    telemetryMeta: { chartId: 'box-plot', viewportWidth: width, viewportHeight: height },
  });
  if (axisDebugEnabled) {
    reportAxisLaneClamp(xLanes);
    reportAxisLaneClamp(yLanes);
  }

  // Box width calculation (same ratio as original)
  const boxWidth = xScale.bandwidth() * (config.boxWidth || 0.4);

  // ─── Tooltip ───────────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  return (
    <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
      <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
        <Group left={margin.left} top={margin.top}>
          {/* Grid lines */}
          {config.showGrid && (
            <GridRows
              scale={yScale}
              width={innerWidth}
              stroke={theme.gridColor}
              strokeOpacity={theme.gridAlpha}
              strokeDasharray="3 3"
              tickValues={yTicks}
            />
          )}

          {/* Y-axis */}
          <AxisLeft
            scale={yScale}
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickLength={yTickLength}
            tickValues={yTicks}
            tickFormat={getCompactTickFormatter(yScale.domain()[1])}
            tickLabelProps={() => ({
              fill: theme.tickColor,
              fontSize: theme.tickSize ?? 13,
              textAnchor: 'end',
              dx: -yTickGap,
              dy: yTickBaselineNudge,
            })}
          />

          {/* X-axis */}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickLength={xTickLength}
            tickValues={xAxis.tickValues}
            tickFormat={xAxis.tickFormat}
            tickComponent={makeTickComponent(xAxis.rotation, { fontSize: xAxis.fontSize, fill: theme.tickColor, fontFamily: theme.fontBody })}
            tickLabelProps={() => ({ opacity: 0 })}
          />

          {/* Axis labels — manual rendering for consistent spacing */}
          <text
            x={-innerHeight / 2}
            y={-yLanes.titleOffsetPx}
            transform="rotate(-90)"
            textAnchor="middle"
            fontSize={theme.labelSize ?? 15}
            fill={theme.labelColor}
          >
            {config.ylabel || ''}
          </text>
          <text
            x={innerWidth / 2}
            y={xAxisTitleY}
            textAnchor="middle"
            fontSize={theme.labelSize ?? 15}
            fill={theme.labelColor}
          >
            {config.xlabel || ''}
          </text>
          {axisDebugEnabled && (
            <AxisLaneDebugOverlay
              lanes={[
                {
                  axisSide: 'left',
                  tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                  titleOffsetPx: yLanes.titleOffsetPx,
                  clamped: yLanes.clamped,
                  spineX: 0,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'bottom',
                  tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                  titleOffsetPx: xLanes.titleOffsetPx,
                  clamped: xLanes.clamped,
                  spineY: innerHeight,
                  spineX1: 0,
                  spineX2: innerWidth,
                },
              ]}
            />
          )}

          {/* Box plots — custom SVG rendering */}
          {data.map((item, idx) => {
            const centerX = (xScale(item.category) || 0) + xScale.bandwidth() / 2;
            const color = categoryColors[idx % categoryColors.length];

            // Y positions via visx scale
            const minY = yScale(item.min);
            const maxY = yScale(item.max);
            const q1Y = yScale(item.q1);
            const q3Y = yScale(item.q3);
            const medianY = yScale(item.median);

            const halfBox = boxWidth / 2;
            const capWidth = boxWidth / 3;

            return (
              <g key={`box-${idx}`}>
                {/* Lower whisker (min to Q1) */}
                <line
                  x1={centerX}
                  y1={minY}
                  x2={centerX}
                  y2={q1Y}
                  stroke={theme.axisColor}
                  strokeWidth={1}
                />

                {/* Upper whisker (Q3 to max) */}
                <line
                  x1={centerX}
                  y1={q3Y}
                  x2={centerX}
                  y2={maxY}
                  stroke={theme.axisColor}
                  strokeWidth={1}
                />

                {/* Min cap */}
                <line
                  x1={centerX - capWidth}
                  y1={minY}
                  x2={centerX + capWidth}
                  y2={minY}
                  stroke={theme.axisColor}
                  strokeWidth={1}
                />

                {/* Max cap */}
                <line
                  x1={centerX - capWidth}
                  y1={maxY}
                  x2={centerX + capWidth}
                  y2={maxY}
                  stroke={theme.axisColor}
                  strokeWidth={1}
                />

                {/* Box (Q1 to Q3) — also the hover target for the full 5-number summary */}
                <rect
                  x={centerX - halfBox}
                  y={q3Y}
                  width={boxWidth}
                  height={q1Y - q3Y}
                  fill={color}
                  stroke={theme.axisColor}
                  strokeWidth={1}
                  rx={theme.barRadius}
                  ry={theme.barRadius}
                  style={{ cursor: 'pointer' }}
                  onMouseMove={(e) => showTip(e, `med: ${item.median}`)}
                />

                {/* Median line */}
                <line
                  x1={centerX - halfBox}
                  y1={medianY}
                  x2={centerX + halfBox}
                  y2={medianY}
                  stroke={theme.axisColor}
                  strokeWidth={1.5}
                />

                {/* Outliers */}
                {config.showOutliers && item.outliers && item.outliers.map((outlier, oIdx) => (
                  <text
                    key={`outlier-${idx}-${oIdx}`}
                    x={centerX}
                    y={yScale(outlier)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={16}
                    fontWeight="bold"
                    fill={theme.foreground}
                  >
                    *
                  </text>
                ))}
              </g>
            );
          })}
        </Group>
      </svg>
      <Tip />
    </div>
  );
}

export default BoxPlotPreview;
